/*
  Lazy video drawers — load the embed when the modal opens, never on page load.

  The theme's <video-media autoplay> hook plays as soon as the element enters the
  viewport (motion's inView). A closed .drawer is visibility:hidden, NOT
  display:none, so it still counts as on-screen: every video modal on the page
  started playing at load.

  Wrapping the embed in <template> does NOT fix that on its own. BaseMedia's
  autoplay path calls play() -> _playerTarget(), which unwraps the template and
  injects the YouTube iframe API itself. The autoplay attribute is what has to
  go; the template only becomes lazy once nothing is pulling it open early.

  So these drawers drop the autoplay attribute and hand hydration to this file.
  The iframe is swapped in when the drawer opens and removed again when it closes
  (removing it is what actually stops playback). An embed whose src already
  carries autoplay=1 therefore still starts the moment the modal opens, exactly
  as it did before — it just no longer starts while the modal is shut.

  Opt in with data-ns-lazy-video on the <video-media> host.
*/
(function () {
  if (window.__nsVideoDrawerLazy) return;
  window.__nsVideoDrawerLazy = true;

  var HOSTS = 'video-media[data-ns-lazy-video]';

  /* Stash the original <template> markup once so teardown can rebuild it. */
  function embedMarkup(host) {
    if (host.__nsEmbedMarkup == null) {
      var tpl = host.querySelector(':scope > template');
      host.__nsEmbedMarkup = tpl ? tpl.innerHTML : '';
    }
    return host.__nsEmbedMarkup;
  }

  function hydrate(host) {
    /* Shopify-hosted video: video_tag already put a <video> in the document, so
       there is nothing to swap in. Dropping autoplay stopped the theme starting
       it, and the drawer opening is the visitor asking to watch — so start it
       here. These are all muted, which keeps the autoplay policy happy. */
    var video = host.querySelector(':scope > video');
    if (video) {
      var started = video.play();
      if (started && started.catch) started.catch(function () {});
      return;
    }

    var tpl = host.querySelector(':scope > template');
    if (!tpl) return; /* already hydrated */
    var markup = embedMarkup(host);
    if (!markup) return;
    tpl.replaceWith(document.createRange().createContextualFragment(markup));
    /* theme.css keeps an iframe hidden until its host is [loaded]. */
    host.setAttribute('loaded', '');
    host.setAttribute('can-play', '');
  }

  function teardown(host) {
    var video = host.querySelector(':scope > video');
    if (video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* Safari throws if metadata is not loaded yet; the pause is what matters. */
      }
      return;
    }

    var frame = host.querySelector(':scope > iframe');
    if (!frame) return;
    var tpl = document.createElement('template');
    tpl.innerHTML = embedMarkup(host);
    frame.replaceWith(tpl);
    host.removeAttribute('loaded');
    host.removeAttribute('can-play');
    host.removeAttribute('playing');
  }

  function drawerFrom(event) {
    var el = event.target;
    return el && el.matches && el.matches('x-drawer') ? el : null;
  }

  /* Descendant search, because the host is not always a direct child — but skip
     any host that belongs to a NESTED drawer, so opening the outer one cannot
     hydrate a video sitting inside a still-closed inner one. */
  function apply(drawer, fn) {
    Array.prototype.forEach.call(drawer.querySelectorAll(HOSTS), function (host) {
      if (host.closest('x-drawer') === drawer) fn(host);
    });
  }

  /* `toggle` fires the moment the open attribute flips, in both directions, and
     it fires before the show/hide transition rather than after it.
     dialog:after-hide does fire on these drawers, but only once the hide
     transition's finished promise resolves — teardown hung off it would leave
     the video playing behind the modal for the length of that animation. */
  document.addEventListener('toggle', function (event) {
    var drawer = drawerFrom(event);
    if (!drawer) return;
    apply(drawer, drawer.open ? hydrate : teardown);
  });
})();
